import type { AH_ToolQuestionPayload, AhToolQuestionOutcome } from '@numeo/types'
import { isAxiosError } from 'axios'
import { getApiErrorMessage } from '@/utils/get-user-error-message'
import type { createStreamIdleGuard } from '@/utils/stream-idle-guard'
import { answerToolQuestion, fetchPendingToolQuestion } from '../api/generalAgentChatApi'
import type { StoreApi } from 'zustand'
import type { GeneralChatStatus } from '../types'
import type { GeneralAgentChatStore } from './generalAgentChatStore'
import { closeMissing, isAnswerable, patchQuestion, resolveQuestion, upsertQuestion, type ToolQuestionStatus } from './toolQuestions'

const ANSWER_FAILED = 'Your answer was not sent. Please try again.'

const statusOf = (error: unknown): number | undefined => (isAxiosError(error) ? error.response?.status : undefined)

/** The store's own pieces the actions use: its state, the end of a wait and the stall watchdog. */
interface QuestionActionDeps {
    get: StoreApi<GeneralAgentChatStore>['getState']
    set: StoreApi<GeneralAgentChatStore>['setState']
    settle: (state: GeneralChatStatus) => void
    watchdog: ReturnType<typeof createStreamIdleGuard>
}

/** The chat store's card-question actions (ADR-048 decisions 9-11), kept beside the store so it stays about the conversation. */
export function createQuestionActions({ get, set, settle, watchdog }: QuestionActionDeps) {
    const setQuestion = (questionId: string, status: ToolQuestionStatus, error?: string) =>
        set((s) => ({ pendingQuestions: patchQuestion(s.pendingQuestions, questionId, status, error) }))

    /** Whether the server still waits on this question; unreadable counts as no, so a card is never reopened blind. */
    const stillWaiting = async (tabId: string, questionId: string): Promise<boolean> =>
        (await fetchPendingToolQuestion(tabId).catch(() => null))?.questionId === questionId

    /** A card asked the user something (`AH_ToolQuestion`, live, replayed or read on open): its turn ended waiting. */
    const questionAsked = (payload: AH_ToolQuestionPayload) => {
        if (get().tabId !== payload.tabId) return
        set((s) => ({ pendingQuestions: upsertQuestion(s.pendingQuestions, payload) }))
        // The turn ended waiting for the user: nothing more comes until they answer.
        if (get().state.status === 'waiting' && isAnswerable(get().pendingQuestions[payload.questionId])) settle({ status: 'ready' })
    }

    /** The question the tab waits on, for a chat opened while its turn waits (the live event went to an earlier page).
     *  An open card the server no longer waits on stopped waiting meanwhile. Unreadable, the chat shows as it is. */
    const readPending = async (tabId: string) => {
        const question = await fetchPendingToolQuestion(tabId).catch(() => undefined)
        if (question === undefined || get().tabId !== tabId) return
        set((s) => ({ pendingQuestions: closeMissing(s.pendingQuestions, tabId, question?.questionId) }))
        if (question) questionAsked(question)
    }

    return {
        readPending,
        questionAsked,

        /** The socket reconnected: the open chat's question events may have been missed, so read the waiting one again. */
        resync: async () => {
            const { tabId } = get()
            if (tabId) await readPending(tabId)
        },

        /** The question stopped waiting (`AH_ToolQuestionResolved`). */
        questionResolved: (questionId: string, outcome: AhToolQuestionOutcome) =>
            set((s) => ({ pendingQuestions: resolveQuestion(s.pendingQuestions, questionId, outcome) })),

        /** The user's Yes (`accept`) or No (`decline`): the waiting turn resumes with it. */
        answerQuestion: async (questionId: string, action: 'accept' | 'decline') => {
            const question = get().pendingQuestions[questionId]
            // The server decides expiry (a 410): this page's clock may be off by minutes.
            if (!question || !isAnswerable(question)) return
            const { tabId } = question
            setQuestion(questionId, 'sending')
            // The resumed turn streams on like any turn, and its first events may beat this answer's response: wait from now.
            if (get().tabId === tabId) {
                set({ state: { status: 'waiting' } })
                watchdog.reset()
            }
            try {
                await answerToolQuestion(tabId, questionId, action)
                if (get().pendingQuestions[questionId]?.status === 'sending') setQuestion(questionId, action === 'accept' ? 'accepted' : 'declined')
            } catch (error) {
                const status = statusOf(error)
                if (status === 410) setQuestion(questionId, 'expired')
                else if (status === 404) setQuestion(questionId, 'gone')
                else if (status !== undefined && status < 500) setQuestion(questionId, 'open', getApiErrorMessage(error, ANSWER_FAILED))
                // No response, or a gateway error (502/503/504): the answer may have reached the server and resumed the turn,
                // so it is answerable again only if the server still waits on it (it does not once an answer was taken).
                else if (!(await stillWaiting(tabId, questionId))) setQuestion(questionId, 'gone')
                else setQuestion(questionId, 'open', getApiErrorMessage(error, ANSWER_FAILED))
                if (get().tabId === tabId && get().state.status === 'waiting') settle({ status: 'ready' })
            }
        },
    }
}
